import { signal, inject, ElementRef, Renderer2, effect, InjectionToken, computed } from '@angular/core';

/**
 * Normalizes the given options.
 *
 * @param options - The options to normalize
 * @internal
 */
const normalizeUseClassOptions = (options) => ({
    initialValue: options?.initialValue ?? true
});
/**
 * Adds the given class to the host element if set to true.
 *
 * @example
 *
 * ```ts
 * import { useClass } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     isLoading = useClass('loading');
 * }
 * ```
 * ```html
 * <my-component></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="loading"></my-component>
 * ```
 *
 * @param className - The name of the class to toggle
 * @param options - A set of options
 */
const useClass = (className, options) => {
    const { initialValue } = normalizeUseClassOptions(options);
    const value = signal(initialValue);
    return bindClass(className, value);
};
/**
 * Toggles a class on the host element based on the value of a signal. Similar to `useClass`, but accepts a boolean signal as an input instead of creating a new one.
 * Will return the signal that has been passed in.
 *
 * @example
 * ```ts
 * import { bindClass } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     isLoading: Signal<boolean> = signal(false);
 *
 *     constructor() {
 *         bindClass('loading', this.isLoading);
 *     }
 * }
 * ```
 * ```html
 * <my-component></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="loading"></my-component>
 * ```
 *
 * @param className - The name of the class to toggle
 * @param value - The signal to determine whether the class should be added or removed. When the signal's value is true, the class will be applied. Else it will be removed.
 */
const bindClass = (className, value) => {
    const element = inject(ElementRef).nativeElement;
    const renderer = inject(Renderer2);
    effect(() => {
        const currentValue = value();
        if (currentValue) {
            renderer.addClass(element, className);
        }
        else {
            renderer.removeClass(element, className);
        }
    });
    return value;
};

const CLASS_SEPARATOR_REGEX = /\s+/;
/**
 * Splits the given classes into an array of classes.
 *
 * @param classes - The classes to split
 */
const splitClasses = (classes) => {
    if (typeof classes !== 'string') {
        return classes;
    }
    return classes ? classes.split(CLASS_SEPARATOR_REGEX) : [];
};
/**
 * Adds the class to the host element.
 * This function uses {@link inject} and has to be called in the injection context of a component or directive.
 *
 * @param clazz - The class to add.
 */
const addClass = (clazz) => {
    const element = inject(ElementRef).nativeElement;
    const renderer = inject(Renderer2);
    renderer.addClass(element, clazz);
};

/**
 * Binds a set of class to the host element based on the value of a signal. Similar to `useClasses`, but accepts a signal as an input instead of creating a new one.
 * Will return the signal that has been passed in.
 *
 * @example
 * ```ts
 * import { bindClasses } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     classes = signal([ 'foo', 'bar' ]);
 *
 *     constructor() {
 *         bindClasses(this.classes);
 *     }
 * }
 * ```
 * ```html
 * <my-component></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component primary focusable"></my-component>
 * ```
 *
 * @example
 * When the signals value changes, the previous classes will be removed.
 * ```ts
 * const classes = signal([ 'foo', 'bar' ]);
 *
 * bindClasses(classes); // <my-component class="foo bar"></my-component>
 * classes.set([ 'baz' ]); // <my-component class="baz"></my-component>
 * ```
 *
 * @example
 * This method can also be used multiple times to bind different sets of classes to the same element.
 * Each use of `bindClasses` will handle the addition & removal of classes on its own, allowing to toggle between different sets of classes.
 *
 * ```ts
 * const classesA = signal([ 'foo', 'bar' ]);
 * const classesB = signal([ 'x', 'y' ]);
 *
 * bindClasses(classesA); // <my-component class="foo bar"></my-component>
 * bindClasses(classesB); // <my-component class="foo bar x y"></my-component>
 *
 * classesA.set([ 'baz' ]); // <my-component class="baz x y"></my-component>
 * classesB.set([ 'a', 'b' ]); // <my-component class="baz a b"></my-component>
 * ```
 *
 * @param value - The signal whose value should be bound
 * @returns The passed in signal (`value` parameter)
 */
const bindClasses = (value) => {
    const element = inject(ElementRef).nativeElement;
    const renderer = inject(Renderer2);
    effect((onCleanup) => {
        const currentClasses = splitClasses(value());
        if (!currentClasses?.length) {
            return;
        }
        for (const clazz of currentClasses) {
            renderer.addClass(element, clazz);
        }
        onCleanup(() => {
            for (const clazz of currentClasses) {
                renderer.removeClass(element, clazz);
            }
        });
    }, {});
    return value;
};
/**
 * Creates a signal that binds its value as a set of classes on the host element.
 *
 * @example Default usage
 * ```ts
 * import { useClasses } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     classes = useClasses(['my-component', 'primary', 'focusable']);
 * }
 * ```
 * ```html
 * <my-component #myComponent></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component primary focusable"></my-component>
 * ```
 *
 * @example String input
 * You may also pass a string as the initial value. The string will be split by spaces and each part will be added as a class.
 * Multiple spaces will be ignored.
 *
 * ```ts
 * const classes = useClasses('my-component primary    focusable');
 * ```
 *
 * @example Programmatically set the value
 *
 * By changing the value of the signal, you can add / remove classes programmatically.
 * Each previously set class will be removed and the new classes will be added.
 * Classes set form somewhere else (e.g. the template) will be untouched, except for ones that are contained in new or previous list.
 *
 * ```ts
 * import { useClasses } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component',
 *     host: {
 *         class: 'my-component'
 *     }
 * })
 * class MyComponent {
 *
 *     classes = useClasses('primary focusable'); // <my-component class="my-component primary focusable"></my-component>
 *
 *     changeClasses() {
 *         this.classes.set('accent'); // <my-component class="my-component accent"></my-component>
 *     }
 * }
 * ```
 *
 * @param initialValue - The initial value of the signal
 * @returns A signal that binds its value as a set of classes on the host element
 */
const useClasses = (initialValue = []) => {
    const value = signal(initialValue);
    return bindClasses(value);
};

/**
 * Injection token for providing a base CSS class.
 */
const BASE_CLASS = new InjectionToken('@bynary/composables/class.base-class');
/**
 * Provide a base CSS class that should be applied to a host.
 * The base class can be injected using the {@link BASE_CLASS} token.
 * This token will be used by {@link useModifier} and {@link useModifierGroup} to generate modifier classes.
 *
 * @see useModifier
 * @see useModifierGroup
 *
 * @example
 * ```ts
 * @Component({
 *    selector: 'my-component',
 *    providers: [
 *        provideBaseClass('my-component')
 *    ]
 * })
 * class MyComponent {
 *     baseClass = inject(BASE_CLASS); // 'my-component'
 * }
 *
 * @param baseClass - The base class to provide.
 * @returns A provider for the {@link BASE_CLASS} token.
 */
const provideBaseClass = (baseClass) => ({
    provide: BASE_CLASS,
    useValue: baseClass
});

/**
 * Normalizes the given options by applying defaults.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeBindModifierOptions = (options) => ({
    baseClass: options?.baseClass ?? inject(BASE_CLASS)
});
/**
 * Uses the given signal to add or remove the given modifier class on the host element.
 * A modifier class requires a base class to be set.
 *
 * To learn more about modifier classes, see [BEM](https://getbem.com/naming/#modifier).
 *
 * @example
 * It is recommended to provide the base class via `provideBaseClass`
 *
 * ```ts
 * import { bindModifier } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component',
 *     providers: [
 *         provideBaseClass('my-component')
 *     ]
 * })
 * class MyComponent {
 *
 *     isLoading: Signal<boolean> = signal(true);
 *
 *     constructor() {
 *         bindModifier('is-loading', this.isLoading);
 *     }
 * }
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component my-component--is-loading"></my-component>
 * ```
 *
 * @example
 * You can also provide the base class per use via the {@link options IBinModifierOptions}:
 *
 * ```ts
 * import { bindModifier } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     isLoading: Signal<boolean> = signal(true);
 *
 *     constructor() {
 *         bindModifier('is-loading', this.isLoading, { baseClass: 'my-component' });
 *     }
 * }
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component my-component--is-loading"></my-component>
 * ```
 *
 * Setting `isLoading` to `false` will remove the modifier class:
 *
 * ```
 * this.isLoading.set(false); // <my-component class="my-component"></my-component>
 * ```
 *
 *
 * @param modifier - The name of the modifier
 * @param apply - The signal whose value should be bound
 * @param options - A set of {@link IBindModifierOptions options}
 * @returns The passed in signal (`apply` parameter)
 */
const bindModifier = (modifier, apply, options) => {
    const { baseClass } = normalizeBindModifierOptions(options);
    if (!baseClass) {
        throw new Error('No base class was provided');
    }
    addClass(baseClass);
    bindClass(`${baseClass}--${modifier}`, apply);
    return apply;
};
/**
 * Normalizes the given options by applying defaults.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeUseModifierOptions = (options) => ({
    ...normalizeBindModifierOptions(options),
    initialValue: options?.initialValue ?? true
});
/**
 * Creates a signal that allows to toggle the given modifier class on the host element.
 *
 * @param modifier - The name of the modifier
 * @param options - A set of {@link IUseModifierOptions options}
 * @returns A signal that allows to toggle the given modifier class on the host element
 */
const useModifier = (modifier, options) => {
    const normalizedOptions = normalizeUseModifierOptions(options);
    const apply = signal(normalizedOptions.initialValue);
    return bindModifier(modifier, apply, options);
};

/**
 * @internal
 * Normalizes the given options by applying defaults.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeOptions = (options) => ({
    baseClass: options?.baseClass ?? inject(BASE_CLASS),
    prefix: options?.prefix
});
/**
 * Binds the value of the given signal to the host element as a modifier class.
 *
 * @example
 * ```ts
 * import { bindModifierGroup } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component',
 *     providers: [
 *         provideBaseClass('my-component')
 *     ]
 * })
 * class MyComponent {
 *
 *     color = signal('red');
 *
 *     constructor() {
 *         bindModifierGroup(this.color);
 *     }
 * }
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component my-component--red"></my-component>
 * ```
 *
 * A change of color will change the corresponding modifier class:
 *
 * ```ts
 * color.set('blue'); // <my-component class="my-component my-component--blue"></my-component>
 * ```
 *
 *
 * @param modifier - The signal to bind
 * @param options - A set of {@link IBindModifierGroupOptions options}
 * @returns The passed in signal (`modifier` parameter)
 * @throws Error - If no base class was provided via `options.baseClass` or via `provideBaseClass`
 */
const bindModifierGroup = (modifier, options) => {
    const { baseClass, prefix } = normalizeOptions(options);
    if (!baseClass) {
        throw new Error('No base class was provided');
    }
    addClass(baseClass);
    const prefixedModifier = prefix ? computed(() => (modifier() ? `${prefix}-${modifier()}` : null)) : modifier;
    const className = computed(() => (prefixedModifier() ? `${baseClass}--${prefixedModifier()}` : null));
    bindClasses(className);
    return modifier;
};
/**
 * Creates a signal that binds its value as a modifier class on the host element.
 *
 * @example
 *
 * ```ts
 * import { useModifierGroup } from '@bynary/composables/class';
 *
 * @Component({
 *     selector: 'my-component',
 *     providers: [
 *         provideBaseClass('my-component')
 *     ]
 * })
 * class MyComponent {
 *
 *     color = useModifierGroup('red');
 * }
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component class="my-component my-component--red"></my-component>
 * ```
 *
 * a change of color will change the corresponding modifier class:
 *
 * ```ts
 * color.set('blue'); // <my-component class="my-component my-component--blue"></my-component>
 * ```
 *
 * @param initialValue - The initial modifier
 * @param options - A set of {@link IBindModifierGroupOptions options}
 * @returns A signal that binds its value as a modifier class on the host element
 * @throws Error - If no base class was provided via `options.baseClass` or via `provideBaseClass`
 */
const useModifierGroup = (initialValue, options) => {
    const modifier = signal(initialValue);
    bindModifierGroup(modifier, options);
    return modifier;
};

/**
 * @packageDocumentation
 * Composables to bind classes on elements.
 *
 * @module @bynary/composables/class
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BASE_CLASS, bindClass, bindClasses, bindModifier, bindModifierGroup, provideBaseClass, useClass, useClasses, useModifier, useModifierGroup };
